import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';
import { ModelConstants, ParameterValue } from 'shell-models-lib';
import { format } from 'app/shared/utils/string.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IApiIntegrationApiParameters {
    requestParameters?: { [name: string]: string };
    body?: { [name: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
}

@Metamodel.model({
    name: ModelConstants.apiIntegrationActivity,
    referable: true
})
export class ApiIntegrationActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly SERVICE_TYPE: IAttribute;
    public static readonly ITEM_TYPE: IAttribute;
    public static readonly WORKSPACE_ID: IAttribute;
    public static readonly API_REQUEST: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly API_PARAMETERS: IAttribute;

    public readonly stringResources = stringResources;

    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        reference: true,
        type: RestServiceLinkedService.MODEL,
        constraint: {
            validator: ApiIntegrationActivity.validateConnection
        }
    })
    public fabricWebLinkedService: RestServiceLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.FabricWebServiceTypeLabel,
        association: 'SERVICE_TYPE',
        constraint: {
            validator: ApiIntegrationActivity.validateServiceType
        },
        templatable: true
    })
    public serviceType: string;

    @Metamodel.attribute({
        display: stringResources.ItemTypeLabel,
        association: 'ITEM_TYPE',
        constraint: {
            validator: ApiIntegrationActivity.validateItemType
        },
        templatable: true
    })
    public itemType: string;

    @Metamodel.attribute({
        display: stringResources.Workspace,
        association: 'WORKSPACE_ID',
        constraint: {
            validator: ApiIntegrationActivity.validateWorkspace
        },
        templatable: true
    })
    public workspaceId: string;

    @Metamodel.attribute({
        display: stringResources.FabricWebApiRequestLabel,
        association: 'API_REQUEST',
        constraint: {
            validator: ApiIntegrationActivity.validateApiRequest
        },
        templatable: true
    })
    public apiRequest: string;

    @Metamodel.attribute({
        association: 'API_PARAMETERS'
    })
    public apiParameters?: IApiIntegrationApiParameters = {};

    public get isFabricServiceType(): boolean {
        return !this.serviceType || this.serviceType === 'Fabric';
    }

    private static validateServiceType(_apiIntegrationActivity?: ApiIntegrationActivity, value?: string): IViolation[] | undefined {
        if (!value) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.FabricWebServiceTypeLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateItemType(apiIntegrationActivity?: ApiIntegrationActivity, value?: string): IViolation[] | undefined {
        // Only required when serviceType is Fabric
        if (apiIntegrationActivity?.isFabricServiceType && !value) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.ItemTypeLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateWorkspace(apiIntegrationActivity?: ApiIntegrationActivity, value?: string): IViolation[] | undefined {
        // Only required when serviceType is Fabric
        if (apiIntegrationActivity?.isFabricServiceType && !value) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.Workspace)
                }
            ];
        }
        return undefined;
    }

    private static validateApiRequest(_apiIntegrationActivity?: ApiIntegrationActivity, value?: string): IViolation[] | undefined {
        if (!value) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.FabricWebApiRequestLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateConnection(_apiIntegrationActivity?: ApiIntegrationActivity, linkedService?: LinkedService): IViolation[] | undefined {
        if (!linkedService) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.ConnectionLabel)
                }
            ];
        }
        if (!ApiIntegrationActivity.isSupportedConnection(linkedService)) {
            return [
                {
                    message: () =>
                        format(
                            stringResources.ActivityNotSupportLinkedServiceError,
                            format(stringResources.ModelName_ActivityTemplate, stringResources.FabricWebLabel),
                            format(stringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(linkedService).name)
                        ),
                    messageType: 'unsupported'
                }
            ];
        }
        return undefined;
    }

    private static isSupportedConnection(linkedService: LinkedService): boolean {
        const supportedConnections: string[] = [
            Metamodel.modelFor(RestServiceLinkedService).name
        ];
        return supportedConnections.includes(linkedService.getLinkedServiceType());
    }
}
